import { useCallback, useRef, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'nucleus_recent_modules';
const MAX_RECENT = 5;

export type RecentModule = {
  path: string;
  label: string;
  visitedAt: number;
};

let listeners: Array<() => void> = [];
let cachedSnapshot: RecentModule[] = readFromStorage();

function readFromStorage(): RecentModule[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function emitChange() {
  cachedSnapshot = readFromStorage();
  listeners.forEach((l) => l());
}

function getSnapshot(): RecentModule[] {
  return cachedSnapshot;
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function recordModuleVisit(path: string, label: string) {
  const normalized = path.split('?')[0];
  if (normalized === '/' || !normalized) return;

  const current = readFromStorage();
  const filtered = current.filter((m) => m.path !== normalized);
  const updated: RecentModule[] = [
    { path: normalized, label, visitedAt: Date.now() },
    ...filtered,
  ].slice(0, MAX_RECENT);

  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  emitChange();
}

export function useRecentModules(): RecentModule[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useClearRecentModules() {
  return useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    emitChange();
  }, []);
}
